import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api/v1`;
export const ROOT_API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API });

// Feb 2026 R293zze · Mark every outbound request with `X-Client` so
// the backend can apply the mobile session-timeout policy (and any
// future client-aware behaviour). Order of precedence:
//   1. Build-time env `REACT_APP_CLIENT` ("mobile" | "web") — set this
//      in the mobile WebView wrapper's build pipeline.
//   2. localStorage `co_client` — set once by the native shell on
//      first launch (so a single bundle can serve both).
//   3. UA sniff for iPhone/iPad/Android mobile browsers — the broad
//      net so public mobile users also pick up the longer window.
//   4. Default: "web".
function _detectClientKind() {
  try {
    const build = (process.env.REACT_APP_CLIENT || "").toLowerCase();
    if (build === "mobile" || build === "web") return build;
    const stored = (localStorage.getItem("co_client") || "").toLowerCase();
    if (stored === "mobile" || stored === "web") return stored;
    const ua = (navigator.userAgent || "").toLowerCase();
    if ((ua.includes("iphone") || ua.includes("ipad") || ua.includes("android")) && ua.includes("mobile")) {
      return "mobile";
    }
  } catch { /* ignore */ }
  return "web";
}
export const CLIENT_KIND = _detectClientKind();
api.defaults.headers.common["X-Client"] = CLIENT_KIND;

// R159vv · Feb 2026 — Paths that legitimately run without an auth
// token (login, signup, public shares). Any other `/api/v1/...` call
// that fires without a token means the session just expired — we
// short-circuit and bounce to /login instead of surfacing the raw
// "Missing bearer token" 401 from the backend.
const ANON_ALLOWED = [
  "/auth/", "/plans", "/branding", "/webhooks/",
  "/r/", "/client/", "/gtm/share/", "/portal", "/policy-runbook",
];

// R159ww · Feb 2026 — Silent token refresh. When the current JWT is
// within `REFRESH_LEEWAY_MS` of expiry we POST to `/auth/refresh` in
// the background (same-tab lock so multiple in-flight requests don't
// dogpile the endpoint). If refresh succeeds the new token is written
// to localStorage before the pending request goes out, so long
// editing sessions never lose auth mid-save.
// Oct 2026 · Backend JWT TTL dropped to 1 hour; refresh when < 30 min
// remain so every active API call during the second half of the hour
// hands out a fresh token.
const REFRESH_LEEWAY_MS = 30 * 60 * 1000; // 30 minutes
let _refreshInFlight = null;

function _decodeExpMs(token) {
  try {
    const b64 = token.split(".")[1];
    const json = JSON.parse(atob(b64.replace(/-/g, "+").replace(/_/g, "/")));
    if (typeof json.exp === "number") return json.exp * 1000;
  } catch { /* malformed */ }
  return 0;
}

// Feb 2026 R293zw · Decode `iat` so we can measure the token's full
// lifetime at issue time (used to tighten the activity window when a
// workspace admin picked a stricter session-timeout policy).
function _decodeIatMs(token) {
  try {
    const b64 = token.split(".")[1];
    const json = JSON.parse(atob(b64.replace(/-/g, "+").replace(/_/g, "/")));
    if (typeof json.iat === "number") return json.iat * 1000;
  } catch { /* malformed */ }
  return 0;
}

async function _refreshTokenNow(token) {
  if (_refreshInFlight) return _refreshInFlight;
  _refreshInFlight = (async () => {
    try {
      const res = await axios.post(`${API}/auth/refresh`, {}, {
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Client": CLIENT_KIND,
        },
      });
      const newTok = res?.data?.token;
      if (newTok) {
        try { localStorage.setItem("co_token", newTok); } catch { /* ignore */ }
        return newTok;
      }
    } catch { /* silent — the doomed request below will 401 and bounce */ }
    return null;
  })();
  try { return await _refreshInFlight; }
  finally { _refreshInFlight = null; }
}

function _isProtectedAppRoute() {
  try {
    const path = window.location.pathname || "";
    return path.startsWith("/app");
  } catch { return false; }
}

// R268~ · Feb 2026 — Public auth pages (reset-password, forgot-password,
// set-password) must never auto-bounce to /login on a 401. AuthProvider
// mounts everywhere and fires `/auth/me`; if there's a stale/invalid
// `co_token` in localStorage the response is 401 → without this guard the
// user is yanked off the reset screen before they can submit a new password.
function _isPublicAuthRoute() {
  try {
    const path = window.location.pathname || "";
    return path === "/reset-password" || path === "/forgot-password" ||
           path === "/set-password" || path === "/signup" ||
           path === "/onboarding";
  } catch { return false; }
}

function _bounceToLogin() {
  try {
    const path = window.location.pathname || "";
    if (path.startsWith("/app")) {
      localStorage.setItem("acq_last_page", path + (window.location.search || ""));
    }
  } catch { /* ignore */ }
  try { localStorage.removeItem("co_token"); } catch { /* ignore */ }
  if (window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
}

api.interceptors.request.use(async (cfg) => {
  let token = localStorage.getItem("co_token");
  const url = (cfg.url || "").toString();
  // Don't try to refresh when we're already hitting /auth/* endpoints.
  const isAuthCall = url.includes("/auth/");
  if (token && !isAuthCall) {
    const expMs = _decodeExpMs(token);
    const iatMs = _decodeIatMs(token);
    // Feb 2026 R293zw · Scale the leeway with the token's lifetime so a
    // workspace that mandates a 15-min window doesn't end up refreshing
    // on every single request. Cap at the 30-min default, floor at the
    // last third of the token's own life.
    let leeway = REFRESH_LEEWAY_MS;
    if (expMs && iatMs && expMs > iatMs) {
      leeway = Math.min(REFRESH_LEEWAY_MS, Math.floor((expMs - iatMs) / 3));
    }
    if (expMs && expMs - Date.now() < leeway && expMs > Date.now()) {
      // Approaching expiry but still valid — refresh silently.
      const fresher = await _refreshTokenNow(token);
      if (fresher) token = fresher;
    } else if (expMs && expMs <= Date.now()) {
      // Already expired — try one refresh; if it fails, bounce.
      const fresher = await _refreshTokenNow(token);
      if (fresher) {
        token = fresher;
      } else if (_isProtectedAppRoute()) {
        _bounceToLogin();
        return Promise.reject(new axios.Cancel("Session expired — redirecting to login"));
      }
    }
  }
  if (token) {
    cfg.headers.Authorization = `Bearer ${token}`;
    return cfg;
  }
  // No token: allow anonymous endpoints, otherwise short-circuit + redirect.
  const isAnon = ANON_ALLOWED.some((p) => url.includes(p));
  if (isAnon) return cfg;
  if (_isProtectedAppRoute()) {
    _bounceToLogin();
    return Promise.reject(new axios.Cancel("Session expired — redirecting to login"));
  }
  return cfg;
});

// R268qq · Feb 2026 — Activity-driven heartbeat. The request-side
// refresh (above) only fires when the app actually hits the API. If a
// user is heads-down typing in a long form (Composer, notes, etc.)
// without triggering XHRs, their token can silently expire and the
// next save loses work. We attach a throttled activity listener that
// bumps `_lastActivityMs`; every `HEARTBEAT_TICK_MS` we check the
// token and refresh it silently if activity was seen recently and the
// token is inside the leeway. Multiple tabs share the same
// localStorage-backed token, so a refresh in one tab benefits all.
// Oct 2026 · Shortened the inter-tick to 2 min and expanded the
// activity window to 55 min — matches the ActivitySessionKeeper tuning
// for the new 1-hour JWT TTL.
const HEARTBEAT_TICK_MS = 2 * 60 * 1000;         // check every 2 min
const ACTIVITY_WINDOW_MS = 55 * 60 * 1000;       // "active" if any input in last 55 min
let _lastActivityMs = Date.now();
let _heartbeatStarted = false;

function _markActivity() {
  _lastActivityMs = Date.now();
}

async function _heartbeatTick() {
  try {
    const token = localStorage.getItem("co_token");
    if (!token) return;
    if (!_isProtectedAppRoute()) return;
    const now = Date.now();
    // Feb 2026 R293zw · Tighten the activity window to the smaller of
    // 55 min and the current token's own lifetime. A workspace that
    // mandates a 15-min timeout should NOT keep refreshing a user who
    // was last active 40 min ago just because our default window was
    // generous.
    const expMs = _decodeExpMs(token);
    const iatMs = _decodeIatMs(token);
    let activityWindow = ACTIVITY_WINDOW_MS;
    if (expMs && iatMs && expMs > iatMs) {
      // Give the user up to 90% of a full token lifetime before we
      // stop refreshing — anything above that overlaps the leeway.
      activityWindow = Math.min(activityWindow, Math.floor((expMs - iatMs) * 0.9));
    }
    if (now - _lastActivityMs > activityWindow) return; // idle — let session expire
    if (!expMs) return;
    // Scale the leeway alongside the token's own lifetime — see request
    // interceptor for the rationale.
    let leeway = REFRESH_LEEWAY_MS;
    if (iatMs && expMs > iatMs) {
      leeway = Math.min(REFRESH_LEEWAY_MS, Math.floor((expMs - iatMs) / 3));
    }
    const untilExp = expMs - now;
    if (untilExp < leeway) {
      await _refreshTokenNow(token);
    }
  } catch { /* silent */ }
}

function _startHeartbeat() {
  if (_heartbeatStarted) return;
  if (typeof window === "undefined" || typeof document === "undefined") return;
  _heartbeatStarted = true;
  // Throttle listeners — mark activity at most once per second.
  let _throttleUntil = 0;
  const throttledMark = () => {
    const now = Date.now();
    if (now < _throttleUntil) return;
    _throttleUntil = now + 1000;
    _markActivity();
  };
  const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "focus"];
  events.forEach((ev) => {
    try { window.addEventListener(ev, throttledMark, { passive: true, capture: true }); }
    catch { /* ignore */ }
  });
  // Also treat a returning tab as fresh activity.
  try {
    document.addEventListener("visibilitychange", () => {
      if (!document.hidden) { _markActivity(); _heartbeatTick(); }
    });
  } catch { /* ignore */ }
  // Tick loop.
  try { setInterval(_heartbeatTick, HEARTBEAT_TICK_MS); }
  catch { /* ignore */ }
}

try { _startHeartbeat(); } catch { /* SSR-safe */ }

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      const path = window.location.pathname;
      // R148d · Feb 2026 — Only auto-redirect on 401 when the user was
      // on a protected /app/* surface. Public / anonymous surfaces
      // shouldn't loop back to /login on their own 401s.
      // R159vv · Feb 2026 — No longer gate on `hadToken`. If a 401
      // ever fires on /app/*, the session is dead — bounce.
      // R268~ · Feb 2026 — Add /reset-password, /forgot-password,
      // /set-password to the skip-list. A stale token in localStorage
      // makes AuthProvider fire /auth/me on mount; without this guard
      // the reset screen redirects to /login before the user can submit.
      if (!path.startsWith("/login") && !path.startsWith("/signup") && !path.startsWith("/r/") && !path.startsWith("/client/") && !path.startsWith("/gtm/share/") && !path.startsWith("/share/") && !path.startsWith("/f/") && !path.startsWith("/portal/") && !path.startsWith("/feedback/") && !path.startsWith("/sign/") && !path.startsWith("/book/") && !path.startsWith("/track/") && !path.startsWith("/enquire/") && !path.startsWith("/enquire-w/") && !path.startsWith("/e/") && !path.startsWith("/deliver/") && !path.startsWith("/email/") && path !== "/" && !path.startsWith("/pricing") && !_isPublicAuthRoute()) {
        _bounceToLogin();
      }
    }
    return Promise.reject(err);
  },
);

export default api;
